import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import {
  useUploadSitePlan,
  useResetSitePlan,
} from "../../hooks/query/useUploadSitePlan";

interface ActivityTabSitePlanProps {
  propertyId: string;
  sitePlanImage?: string | null;
  setSitePlanImage?: React.Dispatch<React.SetStateAction<string | null>>;

  sitePlanPoints?: { x: number; y: number }[];
  setSitePlanPoints?: React.Dispatch<
    React.SetStateAction<{ x: number; y: number }[]>
  >;

  isMatchingSitePlanPoints?: boolean;
  setIsMatchingSitePlanPoints?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ActivityTabSitePlan: React.FC<ActivityTabSitePlanProps> = ({
  // propertyId,
  sitePlanImage,
  setSitePlanImage,
  sitePlanPoints,
  setSitePlanPoints,
  isMatchingSitePlanPoints,
  setIsMatchingSitePlanPoints,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: uploadSitePlan, isLoading } = useUploadSitePlan();
  const { mutateAsync: resetSitePlan, isLoading: isResetting } =
    useResetSitePlan();
  const { propertyId } = useParams<{ propertyId: string }>();

  useEffect(() => {
    console.log("Upadated Site Plan Image:", sitePlanImage);
  }, [sitePlanImage]);

  useEffect(() => {
    if (propertyId) {
      fetchSitePlan();
    }
  }, [propertyId]);

  const fetchSitePlan = async () => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/site-plans/${propertyId}`
      );
      if (res.status === 404) {
        console.log("Site Plan not found for propertyId:", propertyId);
        return;
      }
      const data = await res.json();
      console.log("Site Plan Data:", data.sitePlanImageUrl);
      setSitePlanImage?.(data.sitePlanImageUrl);
    } catch (error) {
      console.error("Error fetching site plan:", error);
    }
  };

  // Safely get the number of pinned points (defaulting to 0 if undefined)
  const pointsCount = sitePlanPoints?.length ?? 0;

  const handleSitePlanClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (pointsCount >= 2) return; // only 2 points allowed

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setSitePlanPoints?.((prev) => [...(prev || []), { x, y }]);
  };

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const handlePointMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    index: number
  ) => {
    e.stopPropagation(); // prevent triggering the sitePlanClick
    setDraggingIndex(index);
  };

  /**
   * Called when the user moves the mouse over the containerRef.
   * If we are dragging a point, update its position.
   */
  const handleContainerMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (draggingIndex === null) return;
    if (!containerRef.current) return;
    if (!setSitePlanPoints) return; // early return if function is missing
    if (!sitePlanPoints) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    // Adjust mouse coordinates relative to container’s top-left
    const x = e.clientX - containerRect.left;
    const y = e.clientY - containerRect.top;

    setSitePlanPoints((prev) => {
      if (!prev) return [];
      const newPoints = [...prev];
      newPoints[draggingIndex] = { x, y };
      return newPoints;
    });
  };

  /**
   * Called when user releases the mouse or leaves the container.
   * Stop dragging the point.
   */
  const handleContainerMouseUp = () => {
    setDraggingIndex(null);
  };

  const handleClearPoints = () => {
    setSitePlanPoints?.([]);
    toast.info("Points Cleared. You can re-pick new points.");
  };

  const handleResetSitePlan = async () => {
    if (!propertyId) {
      toast.error("Property ID is missing! Unable to reset site plan.");
      console.error("Missing propertyId while resetting site plan");
      return;
    }

    try {
      console.log("Resetting site plan for propertyId:", propertyId);
      await resetSitePlan({ propertyId });

      // Clear the site plan image
      setSitePlanImage?.(null);

      // Clear the site plan points
      setSitePlanPoints?.([]);
      setPreviewUrl(null);

      // Also reset the matching state if it was active
      if (isMatchingSitePlanPoints) {
        setIsMatchingSitePlanPoints?.(false);
      }

      toast.success(" Site plan reset successfully!");

      // Clear the selected file state
      setSelectedFile(null);
    } catch (error) {
      toast.error("Failed to reset site plan.");
      console.error("Reset error:", error);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first.");
      return;
    }

    if (!propertyId) {
      toast.error(" Property ID is missing! Unable to upload site plan.");
      console.error("Missing propertyId while uploading site plan");
      return;
    }

    try {
      console.log("Uploading site plan for propertyId:", propertyId);
      const response = await uploadSitePlan({ propertyId, file: selectedFile });
      toast.success(" Site plan uploaded successfully!");

      if (response && response.sitePlanImageUrl) {
        console.log(
          " Received site plan image URL:",
          response.sitePlanImageUrl
        );

        // Force reloading the image by adding a timestamp
        setSitePlanImage?.(response.sitePlanImageUrl + `?t=${Date.now()}`);
      } else {
        console.error(" No sitePlanImageUrl returned in response:", response);
        toast.error("Image uploaded but not returned. Try reloading.");
      }
      setPreviewUrl(null);
      setSelectedFile(null);
    } catch (error) {
      toast.error("Failed to upload site plan.");
      console.error("Upload error:", error);
    }
  };

  const displayImage = previewUrl || sitePlanImage;

  return (
    <Box>
      <Typography
        variant="subtitle1"
        sx={{ marginBottom: "12px", fontWeight: "bold" }}
      >
        Site Plan Upload
      </Typography>

      {/* File Input & Upload Button */}
      <input
        type="file"
        hidden
        id="upload-site-plan"
        onChange={handleFileChange}
      />

      <Box display="flex" justifyContent="center" alignItems="center" gap={3}>
        <label htmlFor="upload-site-plan">
          <Button
            variant="contained"
            component="span"
            disabled={isLoading}
            sx={{
              width: 140,
              height: 36,
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} />
            ) : sitePlanImage ? (
              "Change File"
            ) : (
              "Choose File"
            )}
          </Button>
        </label>

        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={isLoading || !selectedFile}
          sx={{
            width: 140,
            height: 36,
          }}
        >
          {isLoading ? <CircularProgress size={20} /> : "Upload"}
        </Button>
      </Box>

      {/* Selected File Info */}
      {selectedFile && (
        <Typography
          variant="body2"
          sx={{ marginTop: "8px", textAlign: "center" }}
        >
          Selected File: {selectedFile.name}
        </Typography>
      )}

      {/* Display the site plan & allow the user to pick 2 points */}
      {displayImage && (
        <Box
          ref={containerRef}
          sx={{
            position: "relative",
            width: "100%",
            height: 300,
            overflow: "auto",
            marginTop: "12px",
          }}
          onMouseMove={handleContainerMouseMove}
          onMouseUp={handleContainerMouseUp}
          onMouseLeave={handleContainerMouseUp}
        >
          <img
            src={displayImage}
            alt="Site Plan"
            style={{ maxWidth: "100%", maxHeight: "300px" }}
            onClick={handleSitePlanClick}
          />
          {(sitePlanPoints || []).map((pt, idx) => (
            <div
              key={idx}
              onMouseDown={(e) => handlePointMouseDown(e, idx)}
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                position: "absolute",
                left: pt.x - 4,
                top: pt.y - 4,
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: hoveredIndex === idx ? "green" : "red",
                cursor: "grab",
                userSelect: "none",
                transition: "background-color 0.2s ease-in-out",
              }}
            />
          ))}
        </Box>
      )}

      <Typography variant="body2" sx={{ marginTop: "12px" }}>
        {`Pinned points on Site Plan: ${pointsCount}/2`}
      </Typography>

      <Box sx={{ display: "flex", gap: 2, marginTop: "12px" }}>
        {pointsCount === 2 && (
          <Button
            variant="outlined"
            onClick={() => setIsMatchingSitePlanPoints?.(true)}
          >
            Match Points on Map
          </Button>
        )}
        {pointsCount > 0 && (
          <Button variant="outlined" onClick={handleClearPoints}>
            Clear Points
          </Button>
        )}
        {sitePlanImage && (
          <Button
            variant="contained"
            onClick={handleResetSitePlan}
            disabled={isResetting}
            color="secondary"
          >
            {isResetting ? <CircularProgress size={20} /> : "Reset Site Plan"}
          </Button>
        )}
      </Box>

      {isMatchingSitePlanPoints && (
        <Typography variant="body2" sx={{ marginTop: "12px", color: "orange" }}>
          Matching in progress: now pick exactly 2 points on the map!
        </Typography>
      )}
    </Box>
  );
};

export default ActivityTabSitePlan;
